import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Navigation, Truck } from 'lucide-react';
interface Props {
  pickup?: string;
  dropoff?: string;
  singleLocation?: boolean;
  /** Approximate distance/time hints. */
  distance?: string;
  eta?: string;
}
/**
 * Stylised route preview map. SVG-based, no external map dependencies.
 * Renders pickup and dropoff pins with an animated dashed route between them.
 * For single-location services (recovery, roadside) only a single pin is shown.
 */
export function RoutePreviewMap({
  pickup,
  dropoff,
  singleLocation,
  distance,
  eta
}: Props) {
  const showRoute = !singleLocation && pickup && dropoff;
  return <div className="relative bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-603">
      {/* Map background — soft brand-toned gradient + road grid illusion */}
      <div className="relative h-44 sm:h-52 bg-gradient-to-br from-blue-50 via-gray-50 to-orange-50/40 overflow-hidden" data-id="element-604">
        {/* Road grid */}
        <svg viewBox="0 0 400 220" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" data-id="element-605">
          <defs data-id="element-606">
            <pattern id="roadGrid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-607">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e2e8f0" strokeWidth="0.6" data-id="element-608" />
            </pattern>
            <linearGradient id="routeGrad" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-609">
              <stop offset="0%" stopColor="#fb923c" data-id="element-610" />
              <stop offset="100%" stopColor="#1f2937" data-id="element-611" />
            </linearGradient>
          </defs>
          <rect width="400" height="220" fill="url(#roadGrid)" data-id="element-612" />

          {/* Faux highways */}
          <path d="M 0 160 Q 100 150 200 130 T 400 80" stroke="#cbd5e1" strokeWidth="6" fill="none" opacity="0.6" data-id="element-613" />
          <path d="M 0 160 Q 100 150 200 130 T 400 80" stroke="white" strokeWidth="1" strokeDasharray="2 6" fill="none" data-id="element-614" />
          <path d="M 80 220 L 140 140 L 260 100 L 340 40" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.4" data-id="element-615" />
          <path d="M 0 80 L 120 60 L 240 30" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.4" data-id="element-616" />

          {/* Animated route between pickup & dropoff */}
          {showRoute && <>
              <path d="M 70 165 C 130 140, 200 130, 270 75" stroke="url(#routeGrad)" strokeWidth="3.5" fill="none" strokeLinecap="round" data-id="element-617" />
              <motion.path d="M 70 165 C 130 140, 200 130, 270 75" stroke="white" strokeWidth="2" strokeDasharray="4 8" fill="none" strokeLinecap="round" initial={{
            strokeDashoffset: 0
          }} animate={{
            strokeDashoffset: -24
          }} transition={{
            duration: 1.4,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-618" />
            </>}
        </svg>

        {/* Pickup pin */}
        {pickup && <motion.div initial={{
        scale: 0,
        y: 10
      }} animate={{
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        delay: 0.1,
        damping: 14
      }} className="absolute" style={{
        left: singleLocation ? '50%' : '17.5%',
        top: singleLocation ? '50%' : '75%',
        transform: 'translate(-50%, -100%)'
      }} data-id="element-619">
            <div className="relative flex flex-col items-center" data-id="element-620">
              <div className="absolute inset-0 -bottom-2 -left-2 -right-2 -top-2 rounded-full bg-brand-500/30 animate-ping" data-id="element-621" />
              <div className="relative w-9 h-9 rounded-full orange-gradient-bg flex items-center justify-center shadow-lg border-2 border-white" data-id="element-622">
                {singleLocation ? <Navigation className="w-4 h-4 text-white" data-id="element-623" /> : <Truck className="w-4 h-4 text-white" data-id="element-624" />}
              </div>
              <div className="w-1 h-2 bg-gray-900/30 rounded-full mt-0.5" data-id="element-625" />
            </div>
          </motion.div>}

        {/* Dropoff pin */}
        {showRoute && <motion.div initial={{
        scale: 0,
        y: 10
      }} animate={{
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        delay: 0.25,
        damping: 14
      }} className="absolute" style={{
        left: '67.5%',
        top: '34%',
        transform: 'translate(-50%, -100%)'
      }} data-id="element-626">
            <div className="relative flex flex-col items-center" data-id="element-627">
              <div className="relative w-9 h-9 rounded-full bg-gray-900 flex items-center justify-center shadow-lg border-2 border-white" data-id="element-628">
                <MapPin className="w-4 h-4 text-white" fill="currentColor" data-id="element-629" />
              </div>
              <div className="w-1 h-2 bg-gray-900/30 rounded-full mt-0.5" data-id="element-630" />
            </div>
          </motion.div>}

        {/* ETA floating badge */}
        {(eta || distance) && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.4
      }} className="absolute top-3 right-3 bg-white/95 backdrop-blur px-3 py-2 rounded-xl shadow-md border border-gray-100 flex items-center gap-2" data-id="element-631">
            <Clock className="w-3.5 h-3.5 text-brand-500" data-id="element-632" />
            <div data-id="element-633">
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 leading-none" data-id="element-634">
                {singleLocation ? 'Driver ETA' : 'Trip time'}
              </p>
              <p className="text-sm font-display font-bold text-gray-900 leading-tight" data-id="element-635">
                {eta || distance}
              </p>
            </div>
          </motion.div>}
      </div>

      {/* Route labels */}
      <div className="p-4 border-t border-gray-100 bg-white" data-id="element-636">
        {singleLocation ? <div className="flex items-center gap-3" data-id="element-637">
            <div className="w-8 h-8 rounded-lg orange-gradient-bg flex items-center justify-center shrink-0" data-id="element-638">
              <Navigation className="w-4 h-4 text-white" data-id="element-639" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-640">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" data-id="element-641">
                On-site location
              </p>
              <p className="font-bold text-gray-900 truncate" data-id="element-642">
                {pickup || 'Location pending'}
              </p>
            </div>
            {eta && <div className="text-right shrink-0" data-id="element-643">
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" data-id="element-644">
                  ETA
                </p>
                <p className="font-display font-bold text-gray-900" data-id="element-645">{eta}</p>
              </div>}
          </div> : <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-center" data-id="element-646">
            <RoutePoint kind="pickup" label="Pickup" value={pickup || 'Pickup pending'} data-id="element-647" />
            <div className="hidden sm:flex flex-col items-center justify-center" data-id="element-648">
              <svg width="36" height="2" className="text-gray-300" data-id="element-649">
                <line x1="0" y1="1" x2="36" y2="1" stroke="currentColor" strokeDasharray="2 3" data-id="element-650" />
              </svg>
              {distance && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1" data-id="element-651">
                  {distance}
                </p>}
            </div>
            <RoutePoint kind="dropoff" label="Dropoff" value={dropoff || 'Dropoff pending'} align="right" data-id="element-652" />
          </div>}
      </div>
    </div>;
}
function RoutePoint({
  kind,
  label,
  value,
  align
}: {
  kind: 'pickup' | 'dropoff';
  label: string;
  value: string;
  align?: 'right';
}) {
  return <div className={`flex items-center gap-2.5 min-w-0 ${align === 'right' ? 'sm:flex-row-reverse sm:text-right' : ''}`} data-id="element-653">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${kind === 'pickup' ? 'orange-gradient-bg' : 'bg-gray-900'}`} data-id="element-654">
        {kind === 'pickup' ? <Truck className="w-4 h-4 text-white" data-id="element-655" /> : <MapPin className="w-4 h-4 text-white" fill="currentColor" data-id="element-656" />}
      </div>
      <div className="min-w-0" data-id="element-657">
        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" data-id="element-658">
          {label}
        </p>
        <p className="font-bold text-gray-900 text-sm truncate" data-id="element-659">{value}</p>
      </div>
    </div>;
}